import { RIYAL } from "@/lib/currency";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Check, Loader2, ShieldCheck, Info, Building2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getUnitDisplayLabel } from "@/lib/propertyUnitMatrix";
import { useProperties } from "@/hooks/useProperties";
import { useUnits } from "@/hooks/useUnits";
import { useRegaValidate } from "@/hooks/useRega";
import { IAMVerificationBanner } from "./IAMVerificationBanner";
import { formatNumber } from "@/lib/formatters";
import { getRegaSaleTotal } from "@/lib/regaPricing";
import {
  LICENSE_NUMBER_LENGTH,
  isLicenseLengthInvalid,
  isValidateLicenseButtonEnabled,
  stripNonDigits,
} from "./listingWizard.utils";
import type { ListerData, IdType } from "./types";
import { regaIdType } from "./regaIdType";
import type { RegaValidateResponse, RegaLocation } from "@/types/rega";

interface NafathIdentityProp {
  nationalId: string;
  idType: string;
  dobHijri: string | null;
  dobGregorian: string | null;
}

interface ListerStepProps {
  data: ListerData;
  onChange: (data: Partial<ListerData>) => void;
  regaData: RegaValidateResponse | null;
  onRegaValidated: (data: RegaValidateResponse) => void;
  isIAMVerified: boolean;
  onIAMVerify: () => void;
  isPrefilledProperty?: boolean;
  isPrefilledUnit?: boolean;
  nafathIdentity?: NafathIdentityProp | null;
  /** True while property/unit are being resolved from REGA deed number */
  isResolvingProperty?: boolean;
}

export function ListerStep({
  data,
  onChange,
  regaData,
  onRegaValidated,
  isIAMVerified,
  onIAMVerify,
  isPrefilledProperty = false,
  isPrefilledUnit = false,
  nafathIdentity,
  isResolvingProperty = false,
}: ListerStepProps) {
  const { t, language } = useLanguage();
  const regaValidate = useRegaValidate();

  const { data: properties, isLoading: propertiesLoading } = useProperties();
  const { data: units, isLoading: unitsLoading } = useUnits(data.propertyId || undefined);

  // Local validation state for license number
  const [licenseError, setLicenseError] = useState<string | null>(null);

  // Nafath ID pre-fill: when individual and Nafath verified, auto-populate idNumber
  const isNafathPrefilled = data.idType === "individual" && !!nafathIdentity?.nationalId;

  useEffect(() => {
    if (data.idType === "individual" && nafathIdentity?.nationalId && data.idNumber !== nafathIdentity.nationalId) {
      onChange({ idNumber: nafathIdentity.nationalId });
    }
  }, [data.idType, data.idNumber, nafathIdentity?.nationalId, onChange]);

  const handleIdTypeChange = (value: IdType) => {
    if (value === "corporate") {
      onChange({ idType: value, idNumber: "", licenseValidated: false });
    } else {
      onChange({ idType: value, licenseValidated: false });
    }
  };

  const handleValidateLicense = async () => {
    // Client-side validation: ensure idType is selected
    if (!data.idType || (data.idType !== "individual" && data.idType !== "corporate")) {
      console.error("Invalid idType:", data.idType);
      return;
    }

    // REGA advertiser id type — see regaIdType(): national IDs → "1", entities
    // (Iqama / company Unified Number) → "2"; 3 is rejected by REGA. Prefix-driven
    // so a mis-set individual/corporate toggle can never send a wrong 1/3.
    const mappedIdType = regaIdType(data.idType, data.idNumber);

    regaValidate.mutate(
      {
        adLicenseNumber: data.licenseNumber,
        advertiserId: data.idNumber,
        idType: mappedIdType,
      },
      {
        onSuccess: (responseData) => {
          onRegaValidated(responseData);
        },
        onError: () => {
          onChange({ licenseValidated: false });
        },
      },
    );
  };

  const isIdNumberLabel = data.idType === "corporate"
    ? t("الرقم الموحد", "Unified Number")
    : t("رقم الهوية", "ID Number");

  // Helper to get location field from REGA data
  const getRegaLocationField = (field: keyof RegaLocation): string | undefined => {
    if (!regaData?.location || typeof regaData.location === "string") return undefined;
    const val = regaData.location[field];
    return val !== undefined && val !== null ? String(val) : undefined;
  };

  return (
    <div className="space-y-6">
      {/* IAM Verification Banner */}
      <IAMVerificationBanner
        isVerified={isIAMVerified}
        onVerify={onIAMVerify}
      />

      {/* ID Type */}
      <div className="space-y-2">
        <Label>
          {t("نوع الهوية", "ID Type")} <span className="text-destructive">*</span>
        </Label>
        <Select
          value={data.idType}
          onValueChange={handleIdTypeChange}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("اختر نوع الهوية", "Select ID type")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">{t("فرد", "Individual")}</SelectItem>
            <SelectItem value="corporate">{t("شركة", "Corporate")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* ID Number */}
      <div className="space-y-2">
        <Label>
          {isIdNumberLabel} <span className="text-destructive">*</span>
        </Label>
        <div className="relative">
          <Input
            value={data.idNumber}
            onChange={(e) => onChange({ idNumber: e.target.value, licenseValidated: false })}
            placeholder={data.idType === "corporate"
              ? t("أدخل الرقم الموحد", "Enter unified number")
              : t("أدخل رقم الهوية", "Enter ID number")
            }
            readOnly={isNafathPrefilled}
            className={isNafathPrefilled ? "pe-10 bg-muted" : ""}
          />
          {isNafathPrefilled && (
            <ShieldCheck className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-600" />
          )}
        </div>
        {isNafathPrefilled && (
          <p className="text-xs text-green-600">
            {t("تم التعبئة من نفاذ", "Pre-filled from Nafath")}
          </p>
        )}
      </div>

      {/* License Number with Validation */}
      <div className="space-y-2">
        <Label>
          {t("رقم رخصة الإعلان", "AD License Number")} <span className="text-destructive">*</span>
        </Label>
        <div className="flex gap-2">
          <Input
            value={data.licenseNumber}
            inputMode="numeric"
            pattern={`\\d{${LICENSE_NUMBER_LENGTH}}`}
            maxLength={LICENSE_NUMBER_LENGTH}
            onChange={(e) => {
              const digits = stripNonDigits(e.target.value);
              onChange({ licenseNumber: digits, licenseValidated: false });
              setLicenseError(
                isLicenseLengthInvalid(digits)
                  ? t("رقم الرخصة يجب أن يكون 10 أرقام", "License number must be 10 digits")
                  : null,
              );
            }}
            onBlur={() => {
              setLicenseError(
                isLicenseLengthInvalid(data.licenseNumber)
                  ? t("رقم الرخصة يجب أن يكون 10 أرقام", "License number must be 10 digits")
                  : null,
              );
            }}
            placeholder={t("أدخل رقم الرخصة", "Enter license number")}
            className={`flex-1${licenseError ? " border-destructive" : ""}`}
          />
          <Button
            type="button"
            variant={data.licenseValidated ? "outline" : "default"}
            onClick={handleValidateLicense}
            disabled={
              !isValidateLicenseButtonEnabled(
                data.idType,
                data.licenseNumber,
                data.idNumber,
                regaValidate.isPending,
              )
            }
            className={data.licenseValidated
              ? "bg-green-50 text-green-700 border-green-300 hover:bg-green-100 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800"
              : "bg-green-600 hover:bg-green-700"
            }
          >
            {regaValidate.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : data.licenseValidated ? (
              <>
                <Check className="h-4 w-4 me-1" />
                {t("تم التحقق", "Validated")}
              </>
            ) : (
              t("التحقق من الرخصة", "Validate License")
            )}
          </Button>
        </div>
        {licenseError && (
          <p className="text-xs text-destructive">{licenseError}</p>
        )}
      </div>

      {/* REGA Data Summary Card */}
      {regaData && data.licenseValidated && (
        <Card className="border-green-300 bg-green-50/50 dark:bg-green-950/20 dark:border-green-800">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-700 dark:text-green-400">
                {t("بيانات رخصة هيئة العقار", "REGA License Data")}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {regaData.advertisementType && (
                <div>
                  <span className="text-muted-foreground">{t("نوع الإعلان", "Ad Type")}</span>
                  <p className="font-medium">{String(regaData.advertisementType)}</p>
                </div>
              )}
              {regaData.propertyPrice != null && (
                <div>
                  <span className="text-muted-foreground">{t("السعر", "Price")}</span>
                  <p className="font-medium">{formatNumber(getRegaSaleTotal(regaData))}&nbsp;{RIYAL}</p>
                </div>
              )}
              {regaData.propertyType && (
                <div>
                  <span className="text-muted-foreground">{t("نوع العقار", "Property Type")}</span>
                  <p className="font-medium">{String(regaData.propertyType)}</p>
                </div>
              )}
              {regaData.propertyArea != null && (
                <div>
                  <span className="text-muted-foreground">{t("المساحة", "Area")}</span>
                  <p className="font-medium">{String(regaData.propertyArea)} {t("م²", "m²")}</p>
                </div>
              )}
              {regaData.deedNumber && (
                <div>
                  <span className="text-muted-foreground">{t("رقم الصك", "Deed Number")}</span>
                  <p className="font-medium font-mono">{String(regaData.deedNumber)}</p>
                </div>
              )}
              {getRegaLocationField("region") && (
                <div>
                  <span className="text-muted-foreground">{t("المنطقة", "Region")}</span>
                  <p className="font-medium">{getRegaLocationField("region")}</p>
                </div>
              )}
              {getRegaLocationField("city") && (
                <div>
                  <span className="text-muted-foreground">{t("المدينة", "City")}</span>
                  <p className="font-medium">{getRegaLocationField("city")}</p>
                </div>
              )}
              {getRegaLocationField("district") && (
                <div>
                  <span className="text-muted-foreground">{t("الحي", "District")}</span>
                  <p className="font-medium">{getRegaLocationField("district")}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Property/Unit Resolution Status — when REGA is validated */}
      {regaData && data.licenseValidated && (
        <>
          {isResolvingProperty && (
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="text-sm font-medium">
                  {t("جاري البحث عن العقار برقم الصك...", "Searching for property by deed number...")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("البحث في عقارات الشركة", "Searching within company properties")}
                </p>
              </div>
            </div>
          )}

          {!isResolvingProperty && data.regaResolved && (
            <Card className="border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-emerald-600" />
                  <div>
                    <p className="text-sm font-medium">
                      {t("تم العثور على عقار مطابق — تم التحديد مسبقاً", "Matching property found — pre-selected")}
                    </p>
                    {data.regaResolved.propertyName && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {data.regaResolved.propertyName}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Property Selection — always visible so the user can override REGA auto-resolution */}
      <div className="space-y-2">
        <Label>
          {t("العقار", "Property")} <span className="text-destructive">*</span>
        </Label>
        <SearchableSelect
          items={properties ?? []}
          value={data.propertyId}
          onValueChange={(v) => onChange({ propertyId: v ?? "", unitId: "", regaResolved: undefined })}
          getKey={(p) => p.id}
          getLabel={(p) => (language === "ar" ? p.name : (p.name_en || p.name))}
          getSubtitle={(p) => p.district || null}
          getSearchString={(p) => `${p.name ?? ""} ${p.name_en ?? ""} ${p.district ?? ""}`}
          placeholder={
            propertiesLoading
              ? t("جاري التحميل...", "Loading...")
              : t("اختر العقار", "Select property")
          }
          searchPlaceholder={t("ابحث باسم العقار، الحي", "Search by name, district")}
          emptyText={t("لا توجد عقارات مطابقة", "No matching property")}
          disabled={isPrefilledProperty || propertiesLoading || isResolvingProperty}
        />
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Info className="h-3 w-3" />
          {data.regaResolved
            ? t("يمكنك تغيير العقار المختار إذا لزم الأمر", "You can change the selected property if needed")
            : t("ربط الإعلان بعقار ووحدة في نظام دلالي", "Link the ad to a property and unit in Dallali")}
        </p>
      </div>

      {/* Unit Selection — always visible so the user can override REGA auto-resolution */}
      <div className="space-y-2">
        <Label>
          {t("الوحدة", "Unit")} <span className="text-destructive">*</span>
        </Label>
        <SearchableSelect
          items={units ?? []}
          value={data.unitId}
          onValueChange={(v) => onChange({ unitId: v ?? "" })}
          getKey={(u) => u.id}
          getLabel={(u) =>
            `${t("وحدة", "Unit")} ${getUnitDisplayLabel(u, language === "ar" ? "ar" : "en")}`
          }
          getSearchString={(u) => `${u.unit_number ?? ""}`}
          placeholder={
            unitsLoading
              ? t("جاري التحميل...", "Loading...")
              : t("اختر الوحدة", "Select unit")
          }
          searchPlaceholder={t("ابحث برقم الوحدة", "Search by unit number")}
          emptyText={t("لا توجد وحدات متاحة", "No units available")}
          disabled={isPrefilledUnit || !data.propertyId || unitsLoading || isResolvingProperty}
        />
      </div>
    </div>
  );
}
